import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilterList,
  faCaretDown,
  faCaretRight,
  faChevronLeft,
  faChevronRight,
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import CustomDropdown from "../../components/CustomDropdown";
import {
  getLicensesByTenantId,
  getLicensesGroupedByTenantId,
  deleteLicense,
  upsertLicenseBillingGroup,
} from "../../api/Licenses";
import useExporter from "../../hooks/useExporter";

import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import Switch from "../../components/Switch";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";
import PriceFormatter from "../../helpers/PriceFormater";
import { format, parseISO } from "date-fns";

const BASE_ROW_KEY = "base";
const ROW_KEY_SEPARATOR = "__";

const parseRowKey = (rowKey) => {
  const idx = rowKey.indexOf(ROW_KEY_SEPARATOR);
  if (idx === -1) return { licensePublicId: rowKey, suffix: "" };
  return {
    licensePublicId: rowKey.substring(0, idx),
    suffix: rowKey.substring(idx + ROW_KEY_SEPARATOR.length),
  };
};

const LicensesPage = () => {
  const { handleExport } = useExporter();
  const { tenantPublicId, agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedLicenses, setExpandedLicenses] = useState(() => new Set());
  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billingGroups, setBillingGroups] = useState([]);
  const [licensesLoaded, setLicensesLoaded] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const formatDate = (date) => {
    if (!date) return "";
    try {
      return format(parseISO(date), "yyyy-MM-dd");
    } catch (error) {
      return date;
    }
  };

  const buildRowKey = (license, billingGroupRow) =>
    `${license.licensePublicId}${ROW_KEY_SEPARATOR}${billingGroupRow.licenseBillingGroupPublicId || BASE_ROW_KEY}`;

  const toggleExpand = (licensePublicId) => {
    const isCurrentlyExpanded = expandedLicenses.has(licensePublicId);

    setExpandedLicenses((prev) => {
      const next = new Set(prev);
      if (next.has(licensePublicId)) {
        next.delete(licensePublicId);
      } else {
        next.add(licensePublicId);
      }
      return next;
    });

    // Collapsing hides the sub-rows — clear any selections that belonged to them
    // so the modal can't operate on invisible rows.
    if (isCurrentlyExpanded) {
      setSelectedRows((rows) =>
        rows.filter(
          (rowKey) => parseRowKey(rowKey).licensePublicId !== licensePublicId
        )
      );
    }
  };

  const toggleRow = (rowKey) => {
    setSelectedRows((prev) => {
      // Selection must stay within a single license (modal moves quantity within one license)
      const { licensePublicId } = parseRowKey(rowKey);
      const prefix = `${licensePublicId}${ROW_KEY_SEPARATOR}`;
      const sameLicense = prev.filter((k) => k.startsWith(prefix));
      if (sameLicense.includes(rowKey)) {
        return sameLicense.filter((k) => k !== rowKey);
      }
      return [...sameLicense, rowKey];
    });
  };

  const selectedLicenseInfo = useMemo(() => {
    if (selectedRows.length === 0) {
      return { license: null, rows: [], maxQuantity: 0 };
    }
    const { licensePublicId: licensePart } = parseRowKey(selectedRows[0]);
    const license = licenses.find((l) => l.licensePublicId === licensePart);
    if (!license) return { license: null, rows: [], maxQuantity: 0 };

    const rows = selectedRows
      .map((key) => {
        const { suffix } = parseRowKey(key);
        return license.billingGroups.find(
          (bg) =>
            (bg.licenseBillingGroupPublicId || BASE_ROW_KEY) === suffix
        );
      })
      .filter(Boolean);

    const maxQuantity = rows.reduce((sum, r) => sum + r.quantity, 0);
    return { license, rows, maxQuantity };
  }, [selectedRows, licenses]);

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(
          agreementPublicId,
          accessToken
        );
        setBillingGroups(Array.isArray(groups) ? groups : []);
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
        setBillingGroups([]);
      }
    };

    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  useEffect(() => {
    if (tenantPublicId) {
      handleGetLicensesByTenant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantPublicId]);

  useEffect(() => {
    const handleLicenseUpdate = () => {
      handleGetLicensesByTenant();
    };

    window.addEventListener("licenseUpdated", handleLicenseUpdate);
    return () => {
      window.removeEventListener("licenseUpdated", handleLicenseUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantPublicId]);

  const handleGetLicensesByTenant = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getLicensesGroupedByTenantId(
        tenantPublicId,
        accessToken
      );
      setLicenses(Array.isArray(data) ? data : []);
      setLicensesLoaded(true);
    } catch (error) {
      console.error(error);
      setLicensesLoaded(true);
    }
  };

  const filteredLicenses = useMemo(() => {
    if (!search) return licenses;
    const s = search.toLowerCase();
    return licenses.filter((l) => {
      if (l.name?.toLowerCase().includes(s)) return true;
      if (l.licensePublicId?.toLowerCase().includes(s)) return true;
      if (l.mpn?.toLowerCase().includes(s)) return true;
      return l.billingGroups?.some((bg) =>
        bg.billingGroupName?.toLowerCase().includes(s)
      );
    });
  }, [licenses, search]);

  useEffect(() => {
    setPage(1);
  }, [search, rowsPerPage]);

  const numberOfPages = Math.max(
    1,
    Math.ceil(filteredLicenses.length / rowsPerPage)
  );
  const paginatedLicenses = filteredLicenses.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const startRow = filteredLicenses.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endRow = Math.min(page * rowsPerPage, filteredLicenses.length);

  const handleOpenModal = () => {
    if (selectedRows.length === 0) {
      toast.error("No licenses selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleSelectBillingGroup = (billingGroupName) => {
    setSelectedBillingGroup(
      billingGroups.find((group) => group.name === billingGroupName)
        ?.billingGroupPublicId || null
    );
  };

  const handleChangeBillingGroup = async () => {
    if (!selectedBillingGroup || selectedRows.length === 0) {
      toast.error("Please select license and a billing group.");
      return;
    }

    const { license, rows } = selectedLicenseInfo;
    if (!license) {
      toast.error("Could not resolve selected license.");
      return;
    }

    try {
      const accessToken = await getAccessToken();

      await Promise.all(
        rows.map((row) =>
          upsertLicenseBillingGroup(
            row.licenseBillingGroupPublicId,
            license.licensePublicId,
            selectedBillingGroup,
            selectedQuantity,
            accessToken
          )
        )
      );

      toast.success(
        "Billing group updated successfully for selected licenses!",
        {
          iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
        }
      );

      handleGetLicensesByTenant();
      setSelectedRows([]);
      setSelectedQuantity("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update billing group:", error);
      toast.error("Failed to update billing group for selected licenses.");
    }
  };

  const handleDeleteLicense = async (licensePublicId) => {
    const toastId = toast.loading("Deleting license...");
    try {
      const accessToken = await getAccessToken();
      await deleteLicense(licensePublicId, accessToken);
      await handleGetLicensesByTenant();
      toast.success("License deleted", {
        id: toastId,
        iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
      });
    } catch (error) {
      toast.error("Failed to delete license", { id: toastId });
      console.error(error);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const accessToken = await getAccessToken();
      const flat = await getLicensesByTenantId(tenantPublicId, accessToken);
      if (!flat || flat.length === 0) {
        toast.error("No licenses to export");
        return;
      }

      const enriched = flat.map((license) => ({
        ...license,
        friendlyUnitPrice: PriceFormatter(license.unitPrice, license.currency),
        friendlyPrice: PriceFormatter(license.price, license.currency),
      }));

      const exportData = enriched.map(
        ({
          name,
          friendlyUnitPrice,
          quantity,
          friendlyPrice,
          billingGroupPublicId,
          billingGroupName,
          billingGroupStatus,
          validationPeriod,
          validationPeriodUnit,
          expirationDate,
          quantityUsed,
          updatedBy,
          updatedDateTimeUTC,
        }) => ({
          Name: name,
          Price: friendlyUnitPrice,
          Quantity: quantity,
          "Total price": friendlyPrice,
          "Billing group ID": billingGroupPublicId,
          "Billing group name": billingGroupName,
          "Billing group status": billingGroupStatus,
          "Validation period": validationPeriod,
          "Validation period unit": validationPeriodUnit,
          "Expiration date": expirationDate,
          "In use": quantityUsed,
          "Updated by": updatedBy,
          "Updated date": updatedDateTimeUTC,
        })
      );

      const currentDate = new Date().toISOString().split("T")[0];
      handleExport({ Licenses: exportData }, `Licenses_${currentDate}`);
    } catch (error) {
      console.error("Failed to export licenses:", error);
      toast.error("Failed to export licenses");
    }
  };

  const renderBillingGroupBadge = (license) => {
    const groupsWithName = license.billingGroups.filter(
      (bg) => bg.billingGroupName
    );
    if (groupsWithName.length === 0) {
      return <span className="text-gray-400">—</span>;
    }
    if (groupsWithName.length === 1) {
      return groupsWithName[0].billingGroupName;
    }
    return (
      <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded">
        {groupsWithName.length} billing groups
      </span>
    );
  };

  const COLUMN_COUNT = 13;

  return (
    <>
      <HeaderBar />

      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />

          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Licenses"} loading={!licensesLoaded} />
            </div>

            <div
              className={`${!licensesLoaded ? "loading" : ""} page-content`}
            >
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Licenses</h2>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn btn-outline-primary-small"
                    onClick={handleOpenModal}
                    disabled={selectedRows.length === 0}
                  >
                    Update billing group
                  </button>
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Billing group, License ID"}
                      value={search}
                      onChange={(v) => setSearch(v.target.value)}
                    />
                    <button
                      className="btn-outline-primary-small ml-4"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        <th className="w-8"></th>
                        <th className="text-left">Name</th>
                        <th className="text-left">Price</th>
                        <th className="text-left">Unit price</th>
                        <th className="text-left">Customer price</th>
                        <th className="text-left">Customer unit price</th>
                        <th className="text-left">Quantity</th>
                        <th className="text-left">In use</th>
                        <th className="text-left">Billing groups</th>
                        <th className="text-left">Billing period</th>
                        <th className="text-left">Renewal date</th>
                        <th className="text-left">Updated</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLicenses.length === 0 ? (
                        <tr>
                          <td
                            colSpan={COLUMN_COUNT}
                            className="text-center py-6 text-gray-500"
                          >
                            No licenses found
                          </td>
                        </tr>
                      ) : (
                        paginatedLicenses.map((license) => {
                          const isExpanded = expandedLicenses.has(
                            license.licensePublicId
                          );
                          const hasMultipleGroups =
                            license.billingGroups.length > 1;
                          const singleGroupRow =
                            !hasMultipleGroups && license.billingGroups[0];
                          const singleGroupRowKey = singleGroupRow
                            ? buildRowKey(license, singleGroupRow)
                            : null;
                          return (
                            <React.Fragment key={license.licensePublicId}>
                              <tr
                                className={`border-b ${
                                  hasMultipleGroups
                                    ? "cursor-pointer hover:bg-blue-50"
                                    : ""
                                }`}
                                onClick={() =>
                                  hasMultipleGroups &&
                                  toggleExpand(license.licensePublicId)
                                }
                              >
                                <td
                                  className="py-2 px-3"
                                  onClick={(e) =>
                                    !hasMultipleGroups && e.stopPropagation()
                                  }
                                >
                                  {hasMultipleGroups ? (
                                    <FontAwesomeIcon
                                      icon={
                                        isExpanded ? faCaretDown : faCaretRight
                                      }
                                      className="text-gray-500"
                                    />
                                  ) : singleGroupRowKey ? (
                                    <Switch
                                      type="checkbox"
                                      checked={selectedRows.includes(
                                        singleGroupRowKey
                                      )}
                                      onChange={() =>
                                        toggleRow(singleGroupRowKey)
                                      }
                                    />
                                  ) : null}
                                </td>
                                <td className="py-2 px-3">{license.name}</td>
                                <td className="py-2 px-3">
                                  {PriceFormatter(
                                    license.price,
                                    license.currency
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {PriceFormatter(
                                    license.unitPrice,
                                    license.currency
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {PriceFormatter(
                                    license.customerPrice,
                                    license.currency
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {PriceFormatter(
                                    license.customerUnitPrice,
                                    license.currency
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {license.totalQuantity}
                                </td>
                                <td className="py-2 px-3">
                                  {license.quantityUsed > 0
                                    ? license.quantityUsed
                                    : "N/A"}
                                </td>
                                <td className="py-2 px-3">
                                  {renderBillingGroupBadge(license)}
                                </td>
                                <td className="py-2 px-3">
                                  {license.friendlyBillingPeriod}
                                </td>
                                <td className="py-2 px-3">
                                  {formatDate(license.subscriptionRenewalDate)}
                                </td>
                                <td className="py-2 px-3">
                                  {license.updatedBy
                                    ? `${license.updatedBy} ${formatDate(
                                        license.updatedDateTimeUTC
                                      )}`
                                    : formatDate(license.updatedDateTimeUTC)}
                                </td>
                                <td
                                  className="py-2 px-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CustomDropdown
                                    isRight={true}
                                    ContentComponent={() => (
                                      <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                        <Link
                                          to={`/${agreementPublicId}/${tenantPublicId}/licenses/${license.licensePublicId}`}
                                          className="block px-4 py-2 text-sm text-black hover:bg-gray-200 w-full text-left rounded-lg"
                                        >
                                          <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            className="mr-4"
                                          />
                                          Edit
                                        </Link>
                                        <button
                                          className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-200 w-full text-left rounded-lg"
                                          onClick={() =>
                                            handleDeleteLicense(
                                              license.licensePublicId
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={faCircleMinus}
                                            className="mr-4"
                                          />
                                          Delete
                                        </button>
                                      </div>
                                    )}
                                  >
                                    <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                      <FontAwesomeIcon icon={faEllipsis} />
                                    </div>
                                  </CustomDropdown>
                                </td>
                              </tr>
                              {isExpanded && hasMultipleGroups && (
                                <tr>
                                  <td
                                    colSpan={COLUMN_COUNT}
                                    className="p-0"
                                  >
                                    <div className="bg-gray-50 px-6 py-4">
                                      <h4 className="text-sm font-semibold mb-2">
                                        Billing groups for {license.name}
                                      </h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-gray-600 border-b">
                                            <th className="py-2 px-3 w-8"></th>
                                            <th className="py-2 px-3 text-left">
                                              Billing group
                                            </th>
                                            <th className="py-2 px-3 text-left">
                                              Cost split
                                            </th>
                                            <th className="py-2 px-3 text-left">
                                              Type
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {license.billingGroups.map((bg) => {
                                            const rowKey = buildRowKey(
                                              license,
                                              bg
                                            );
                                            const isSelected =
                                              selectedRows.includes(rowKey);
                                            return (
                                              <tr
                                                key={rowKey}
                                                className="border-b"
                                              >
                                                <td className="py-2 px-3">
                                                  <Switch
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() =>
                                                      toggleRow(rowKey)
                                                    }
                                                  />
                                                </td>
                                                <td className="py-2 px-3">
                                                  {bg.billingGroupName || (
                                                    <span className="text-gray-400">
                                                      (Unassigned)
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="py-2 px-3">
                                                  {bg.quantity}
                                                </td>
                                                <td className="py-2 px-3">
                                                  {bg.isBaseLicense
                                                    ? "Base"
                                                    : "Split"}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {filteredLicenses.length >= 10 && (
                    <div className="flex gap-4 justify-end items-center text-black pt-4">
                      <p className="text-sm">Rows per page</p>
                      <div className="w-[72px]">
                        <Dropdown
                          className="mb-1"
                          size="small"
                          options={["100", "500", "1000"]}
                          onSelect={(rows) => {
                            setPage(1);
                            setRowsPerPage(parseInt(rows, 10));
                          }}
                          selectedOptions={[rowsPerPage.toString()]}
                        />
                      </div>
                      <p className="text-sm">
                        {`${startRow}-${endRow} of ${filteredLicenses.length}`}
                      </p>
                      <FontAwesomeIcon
                        className={`cursor-pointer ${
                          page === 1 ? "opacity-50" : ""
                        }`}
                        icon={faChevronLeft}
                        onClick={() => page > 1 && setPage(page - 1)}
                      />
                      <FontAwesomeIcon
                        className={`cursor-pointer ${
                          page === numberOfPages ? "opacity-50" : ""
                        }`}
                        icon={faChevronRight}
                        onClick={() =>
                          page < numberOfPages && setPage(page + 1)
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">Change billing group</h2>
          <div className="flex flex-col gap-4">
            <InputField
              label="Quantity"
              type="number"
              value={selectedQuantity}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);

                if (
                  !isNaN(value) &&
                  value <= selectedLicenseInfo.maxQuantity &&
                  value >= 1
                ) {
                  setSelectedQuantity(value);
                } else if (value === 0 || e.target.value === "") {
                  setSelectedQuantity("");
                }
              }}
              placeholder={
                selectedLicenseInfo.license
                  ? `Max: ${selectedLicenseInfo.maxQuantity}`
                  : "Enter quantity"
              }
            />

            <h4 className="text-sm text-bold ">To</h4>

            <Dropdown
              label="Select billing group"
              options={billingGroups.map((group) => group.name)}
              value={
                billingGroups.find(
                  (group) =>
                    group.billingGroupPublicId === selectedBillingGroup
                )?.name || ""
              }
              onSelect={handleSelectBillingGroup}
            />
          </div>
          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default LicensesPage;
